import { useState } from "react";
import RecordModal from "./RecordModal";
import "../reportDashboard.scss";

const RADIUS = 40;
const CIRC = 2 * Math.PI * RADIUS;
const scoreTone = (score) => {
  if (score >= 90) return "kudos";
  if (score >= 80) return "accent";
  if (score >= 70) return "warning";
  return "danger";
};

// Bar tone: four bands so a dimension at 65% doesn't render identically to
// one at 10% (every bar rendering red regardless of how close to passing it
// was is the screenshot bug this fixes). Same 4-band shape as UsersSection's
// engagementTone, different thresholds — deliberately not reused verbatim.
const barTone = (component) => {
  if (!component.max) return "muted";
  const pct = (component.score / component.max) * 100;
  if (pct >= 90) return "kudos";
  if (pct >= 70) return "warning";
  if (pct >= 50) return "corrective";
  return "danger";
};

// Rules that govern the total rather than any one dimension. The per-dimension
// formulas are NOT here — each component arrives from the API with its own
// `formula` string, written beside the arithmetic in SafetyScoreCalculator.cs, so
// there is no second copy of the weights and divisors to drift out of step. These
// five are the surrounding rules that no single dimension owns.
const SCORING_RULES = [
  "A dimension with nothing to measure is marked N/A and leaves the score entirely — its points and its weight both. The remaining dimensions rescale to 100, so absent data can neither help nor hurt the score.",
  "When no dimension is measurable there is no score at all, rather than a 0 — a 0 would read as failing when the honest answer is that the question cannot be asked yet.",
  "The total is summed from unrounded dimension scores and only then rounded, so the rounded values above need not add up to the total exactly.",
  "A large volume of expiring credentials caps the overall score however the other dimensions land: credential severity (expired×3 + critical×2 + high×1) above 20 caps the score at 69, and above 40 at 59.",
  "Everything is computed from live data over the selected date range, with two deliberate exceptions: Inspection Activity always uses a fixed 90-day window, and credentials and lesson/course completion are all-time (they carry no date to filter on).",
];

// "How is this calculated" disclosure behind the banner's info badge. Built on the
// shared RecordModal so it matches every other popup in the dashboard.
//
// GENERIC BY DESIGN: the method only, never this client's numbers. No score, no
// dimension tally, no per-dimension `score`/`max`/`detail` — those all sit on the
// banner behind it, and repeating them here would make the same figure readable in
// two places that can disagree (the bars are rounded, the total is not), and would
// turn an explanation of the model into a second, partial report on one org. What
// is taken from `components` is only the model's own shape: each dimension's label
// and its `formula` string, both constants written beside the arithmetic in
// SafetyScoreCalculator.cs and identical for every client and date range.
const methodModalProps = (components) => ({
  title: "How the Safety Score is calculated",
  // Prose, not record fields: sizes this dialog's paragraph and rule list at the
  // dashboard's 12px body scale without altering the section types anywhere else.
  bodyClass: "rdModal__body--prose",
  sections: [
    {
      type: "note",
      text:
        "Six dimensions, weighted 20 / 20 / 15 / 20 / 10 / 15 to 100 points. Each is scored as a rate "
        + "against a real denominator — never as points awarded for absent data — and the points earned "
        + "across the dimensions that apply are rescaled to a score out of 100.",
    },
    {
      title: "The six dimensions",
      type: "cards",
      rows: components.map((c) => ({ title: c.label, note: c.formula })),
      emptyLabel: "No dimensions",
    },
    { title: "Rules that apply to the total", type: "bullets", items: SCORING_RULES },
  ],
});

// Horizontal banner: ring on the left (vertically centred beside the bars —
// see .rdScoreBanner in reportDashboard.scss), eyebrow/headline/dims copy and
// component bars filling the remaining space on the right — always visible,
// no expand-on-click gate. The banner is self-titled and score-tinted, so
// callers must not wrap it in another rdCard + "Safety Score" title.
const SafetyScoreRing = ({
  score,
  components = [],
  activeDimensions,
  totalDimensions,
  isScorable = true,
  unscorableNote,
}) => {
  const [methodOpen, setMethodOpen] = useState(false);
  // An org with nothing measurable in range has NO score. Rendering 0 would read as
  // "failing" and the old model rendered 85 for having no data, so the unscorable case
  // gets its own treatment: an empty muted ring, a dash instead of a number, and the
  // reason in place of the headline. Every component row still renders as N/A so the
  // reader can see exactly what is missing.
  const scorable = isScorable !== false && score !== null && score !== undefined;
  const safeScore = scorable ? Number(score) || 0 : 0;
  const dash = scorable ? CIRC * (Math.min(100, Math.max(0, safeScore)) / 100) : 0;
  const tone = scorable ? scoreTone(safeScore) : "muted";
  const toneClass = ` is-${tone}`;

  const missingLabels = components
    .filter((c) => c.isNotApplicable)
    .map((c) => c.label);

  const dimsText = !scorable
    ? null
    : totalDimensions > 0
      ? missingLabels.length > 0
        ? `Weighted across ${activeDimensions} of ${totalDimensions} safety dimensions — no data in range for ${missingLabels.join(" and ")}`
        : `Weighted across ${totalDimensions} safety dimensions based on live data`
      : null;

  return (
    <div className={`rdScoreBanner${toneClass}${scorable ? "" : " is-unscorable"}`}>
      <div className={`rdRing${toneClass}`}>
        <svg width="110" height="110" viewBox="0 0 100 100">
          <circle className="rdRing__track" cx="50" cy="50" r={RADIUS} strokeWidth="10" />
          {/* A circle path starts at 3 o'clock, and the quarter-circumference
              dash offset below walks the arc's start back to 12 o'clock on its
              own. An additional rotate(-90) was compounding with it and
              starting the arc at 9 o'clock instead. Butt caps, not round: at a
              low score the arc is short, and round caps add half the 10px
              stroke to each end — enough to overstate a single-digit score. */}
          <circle
            className="rdRing__arc"
            cx="50"
            cy="50"
            r={RADIUS}
            strokeWidth="10"
            strokeDashoffset={CIRC * 0.25}
            style={{ "--rd-dash": `${dash} ${CIRC - dash}` }}
          />
          {scorable ? (
            <>
              <text className="rdRing__score" x="50" y="52" textAnchor="middle" fontSize="26">
                {safeScore}
              </text>
              <text className="rdRing__unit" x="50" y="65" textAnchor="middle" fontSize="8">
                / 100
              </text>
            </>
          ) : (
            <text className="rdRing__score" x="50" y="58" textAnchor="middle" fontSize="26">
              &#8212;
            </text>
          )}
        </svg>
      </div>

      <div className="rdScoreBanner__body">
        <div className="rdScoreBanner__eyebrow">
          Safety Score
          {/* Borrows .rdNotice__icon's circle so this badge is the same "i" the
              draft/disclosure banners use; .rdScoreBanner__info only strips the
              <button> chrome and the eyebrow's uppercase letter-spacing. */}
          <button
            type="button"
            className="rdNotice__icon rdScoreBanner__info"
            onClick={() => setMethodOpen(true)}
            aria-label="How the Safety Score is calculated"
            title="How the Safety Score is calculated"
          >
            i
          </button>
        </div>
        <div className={`rdScoreBanner__headline is-${tone}`}>
          {scorable ? `${safeScore} / 100` : "No Safety Score"}
        </div>
        {dimsText && <div className="rdScoreBanner__dims">{dimsText}</div>}
        {/* Reason stands in for the dims line, which is null when unscorable, so exactly
            one of the two ever renders and the banner keeps its normal vertical rhythm. */}
        {!scorable && (
          <div className="rdScoreBanner__dims">
            {unscorableNote || "Not enough data in this range to calculate a Safety Score"}
          </div>
        )}

        <div className="rdScoreBanner__components">
          {components.map((c) =>
            c.isNotApplicable ? (
              <div className="rdScoreBanner__row" key={c.label}>
                <div className="rdScoreBanner__rowHead">
                  <span>{c.label}</span>
                  <span className="rdValue is-muted">N/A</span>
                </div>
                <div className="rdBar rdBar--full">
                  <div className="rdBar__fill rdBar__fill--hatched" style={{ "--rd-fill": "100%" }} />
                </div>
                <div className="rdScoreBanner__rowDetail">{c.detail}</div>
              </div>
            ) : (
              <div className="rdScoreBanner__row" key={c.label}>
                <div className="rdScoreBanner__rowHead">
                  <span>
                    {c.label}
                    {c.isBonus && <span className="rdFlag is-kudos">bonus</span>}
                  </span>
                  <span className={`rdValue is-${barTone(c)}`}>
                    {c.isBonus ? "+" : ""}
                    {c.score}/{c.max}
                  </span>
                </div>
                <div className="rdBar rdBar--full">
                  <div
                    className={`rdBar__fill is-${barTone(c)}`}
                    style={{ "--rd-fill": `${c.max ? (c.score / c.max) * 100 : 0}%` }}
                  />
                </div>
                <div className="rdScoreBanner__rowDetail">{c.detail}</div>
              </div>
            ),
          )}
        </div>
      </div>

      {/* Mounted only while open, same as every other drill-down in this feature. */}
      {methodOpen && (
        <RecordModal
          open
          onClose={() => setMethodOpen(false)}
          {...methodModalProps(components)}
        />
      )}
    </div>
  );
};

export default SafetyScoreRing;
